import React from 'react';

interface FloatingIconProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className: string;
  animationDelay: string;
  animationDuration: string;
  zIndex?: string;
}

const FloatingIcon: React.FC<FloatingIconProps> = ({
  src,
  alt,
  width,
  height,
  className,
  animationDelay,
  animationDuration,
  zIndex = "z-[1]"
}) => {
  // Determine which float animation to use based on the className
  let animationName = 'float1';
  let rotation = 'rotate-0';

  if (className.includes('right-') && (className.includes('top-[1%]') || className.includes('top-[2%]') || className.includes('top-[3%]'))) {
    animationName = 'float4';
    rotation = 'rotate-1';
  } else if (className.includes('left-') && (className.includes('top-[20%]') || className.includes('top-[22%]') || className.includes('top-[25%]'))) {
    animationName = 'float2';
    rotation = '-rotate-1';
  } else if (className.includes('right-') && (className.includes('top-[20%]') || className.includes('top-[22%]') || className.includes('top-[25%]'))) {
    animationName = 'float5';
    rotation = 'rotate-1';
  } else if (className.includes('left-') && (className.includes('top-[48%]') || className.includes('top-[50%]'))) {
    animationName = 'float3';
    rotation = '-rotate-1';
  } else if (className.includes('right-') && (className.includes('top-[48%]') || className.includes('top-[50%]'))) {
    animationName = 'float6';
    rotation = 'rotate-1';
  } else if (className.includes('left-') && (className.includes('top-[12%]') || className.includes('top-[13%]') || className.includes('top-[14%]'))) {
    rotation = '-rotate-1';
  }

  return (
    <div 
      className={`${className} ${rotation} ${zIndex} transform opacity-80 hover:opacity-100 transition-opacity duration-300`}
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className="w-full h-full object-contain drop-shadow-xl filter brightness-110"
        style={{
          animationDelay,
          animation: `${animationName} ${animationDuration} ease-in-out infinite`,
          transform: 'translateZ(0)'
        }}
      />
    </div>
  );
};

const FloatingIcons: React.FC = () => {
  const heroIcons = [
    {
      src: "https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/a5f0e177-3782-482f-b233-8a6e0244ba45-blogbuster-so/assets/images/next-395755-one.png?",
      alt: "AI Technology Icon",
      width: 120,
      height: 100,
      className: "absolute left-[4%] top-[12%] sm:left-[5%] sm:top-[13%] lg:left-[6%] lg:top-[14%]",
      animationDelay: "0s",
      animationDuration: "6s",
      zIndex: "z-[1]"
    },
    {
      src: "https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/a5f0e177-3782-482f-b233-8a6e0244ba45-blogbuster-so/assets/images/next-617746-four.png?",
      alt: "Machine Learning Icon",
      width: 130,
      height: 110,
      className: "absolute right-[4%] top-[12%] sm:right-[5%] sm:top-[13%] lg:right-[6%] lg:top-[14%]",
      animationDelay: "1s",
      animationDuration: "8s",
      zIndex: "z-[1]"
    },
    {
      src: "https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/a5f0e177-3782-482f-b233-8a6e0244ba45-blogbuster-so/assets/images/next-916092-two.png?",
      alt: "Data Processing Icon",
      width: 248,
      height: 208,
      className: "absolute left-[-4%] top-[32%] sm:left-[-5%] sm:top-[34%] lg:left-[-6%] lg:top-[34%]",
      animationDelay: "2s",
      animationDuration: "7s",
      zIndex: "z-[1]"
    },
    {
      src: "https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/a5f0e177-3782-482f-b233-8a6e0244ba45-blogbuster-so/assets/images/next-933414-five.png?",
      alt: "Neural Network Icon",
      width: 248,
      height: 208,
      className: "absolute right-[-4%] top-[32%] sm:right-[-5%] sm:top-[34%] lg:right-[-6%] lg:top-[34%]",
      animationDelay: "0.5s",
      animationDuration: "6.5s",
      zIndex: "z-[1]"
    },
    {
      src: "https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/a5f0e177-3782-482f-b233-8a6e0244ba45-blogbuster-so/assets/images/next-815741-three.png?",
      alt: "Content Generation Icon",
      width: 190,
      height: 160,
      className: "absolute left-[3%] top-[65%] sm:left-[4%] sm:top-[67%] lg:left-[5%] lg:top-[67%]",
      animationDelay: "1.5s",
      animationDuration: "5s",
      zIndex: "z-[1]"
    },
    {
      src: "https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/a5f0e177-3782-482f-b233-8a6e0244ba45-blogbuster-so/assets/images/next-818064-six.png?",
      alt: "SEO Optimization Icon",
      width: 192,
      height: 178,
      className: "absolute right-[3%] top-[65%] sm:right-[4%] sm:top-[67%] lg:right-[5%] lg:top-[67%]",
      animationDelay: "2.5s",
      animationDuration: "7.5s",
      zIndex: "z-[1]"
    },
  ];

  return (
    <>
      {/* Desktop Floating Icons Only */}
      <div className="pointer-events-none absolute inset-0 hidden overflow-visible lg:block z-[1]">
        {heroIcons.map((icon, index) => (
          <FloatingIcon
            key={index}
            src={icon.src}
            alt={icon.alt}
            width={icon.width}
            height={icon.height}
            className={icon.className}
            animationDelay={icon.animationDelay}
            animationDuration={icon.animationDuration}
            zIndex={icon.zIndex}
          />
        ))}
      </div>

      {/* Mobile Floating Icons - REMOVED */}
      {/* No mobile floating icons - clean mobile experience */}
    </>
  );
};

export default FloatingIcons;
